import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { BarChart, Bar as RBar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api, apiError } from "@/lib/api";
import { fmtNum, fmtMoney, fmtDate, today } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SEV = { Low: "muted", Medium: "primary", High: "warn", Critical: "bad" };

export default function Hindrances() {
  const { pid } = useParams();
  const [rows, setRows] = useState([]);
  const [acts, setActs] = useState([]);
  const [meta, setMeta] = useState({ hindrance_categories: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    activity_id: "", category: "", severity: "Medium", description: "", raised_date: today(),
    impact_days: "", cost_impact: "", responsibility: "Client", status: "Open",
  });

  const load = () => {
    api.get("/hindrances", { params: { project_id: pid } }).then(({ data }) => setRows(data));
    api.get(`/projects/${pid}/wbs`).then(({ data }) => setActs(data.filter((a) => a.total_qty > 0)));
    api.get("/meta").then(({ data }) => setMeta(data));
  };
  useEffect(() => { load(); }, [pid]);

  const save = async () => {
    try {
      await api.post("/hindrances", {
        project_id: pid, ...form, activity_id: form.activity_id || null,
        impact_days: Number(form.impact_days || 0), cost_impact: Number(form.cost_impact || 0),
        closed_date: null,
      });
      toast.success("Hindrance logged with critical-path impact");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const byCat = Object.values(rows.reduce((acc, h) => {
    const k = h.category;
    acc[k] ||= { name: k, days: 0, cost: 0 };
    acc[k].days += Number(h.impact_days || 0);
    acc[k].cost += Number(h.cost_impact || 0);
    return acc;
  }, {})).sort((a, b) => b.days - a.days);

  const critImpact = rows.reduce((s, h) => s + Number(h.critical_path_impact_days || 0), 0);

  return (
    <div data-testid="hindrances-page">
      <PageHead title="Hindrance & Delay Matrix" subtitle="Bottleneck register with severity, critical-path impact days and cost exposure.">
        <Button onClick={() => setOpen(true)} data-testid="hindrance-new-btn"><Plus className="w-4 h-4 mr-1.5" /> Log hindrance</Button>
      </PageHead>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
        <Kpi label="Total logged" value={rows.length} testId="kpi-hindrance-total" />
        <Kpi label="Open" value={rows.filter((r) => r.status === "Open").length} tone="warn" />
        <Kpi label="Delay days claimed" value={fmtNum(rows.reduce((s, r) => s + Number(r.impact_days || 0), 0), 0)} />
        <Kpi label="Critical path impact" value={`${fmtNum(critImpact, 0)} d`} tone="bad" testId="kpi-hindrance-critical" />
        <Kpi label="Cost exposure" value={fmtMoney(rows.reduce((s, r) => s + Number(r.cost_impact || 0), 0))} tone="bad" />
      </div>

      <div className="grid xl:grid-cols-3 gap-3">
        <Panel title="Delay drivers by impact days" testId="hindrance-chart-panel">
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byCat} layout="vertical" margin={{ left: 4, right: 16 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <RBar dataKey="days" name="Impact days" radius={[0, 2, 2, 0]}>
                  {byCat.map((x, i) => <Cell key={i} fill={["#EF4444", "#F59E0B", "#3B82F6", "#8B5CF6", "#10B981"][i % 5]} />)}
                </RBar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Hindrance register" className="xl:col-span-2" testId="hindrance-table-panel">
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full dense-table">
              <thead className="sticky-head">
                <tr><th>Raised</th><th>Category</th><th>Severity</th><th>Activity</th><th className="text-right">Impact d</th><th className="text-right">CP impact</th><th className="text-right">Cost</th><th>Responsibility</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {rows.map((h) => (
                  <tr key={h.id} data-testid={`hindrance-row-${h.id}`}>
                    <td className="num">{fmtDate(h.raised_date)}</td>
                    <td className="max-w-[190px] truncate" title={h.description}>{h.category}</td>
                    <td><Badge2 tone={SEV[h.severity]}>{h.severity}</Badge2></td>
                    <td className="text-muted-foreground max-w-[160px] truncate">{h.activity_name}</td>
                    <td className="num text-right">{h.impact_days}</td>
                    <td className="num text-right text-red-500">{h.critical_path_impact_days || 0}</td>
                    <td className="num text-right">{fmtMoney(h.cost_impact)}</td>
                    <td className="text-muted-foreground">{h.responsibility}</td>
                    <td><Badge2 tone={h.status === "Open" ? "warn" : "good"}>{h.status}</Badge2></td>
                    <td><button onClick={async () => { await api.delete(`/hindrances/${h.id}`); load(); }} className="text-muted-foreground hover:text-red-500" data-testid={`hindrance-delete-${h.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
                {rows.length === 0 ? <tr><td colSpan={10} className="text-muted-foreground">No hindrances logged.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Log hindrance</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="hindrance-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta.hindrance_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="hindrance-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(SEV).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Affected activity</Label>
              <Select value={form.activity_id} onValueChange={(v) => setForm({ ...form, activity_id: v })}>
                <SelectTrigger data-testid="hindrance-activity-select"><SelectValue placeholder="Project level" /></SelectTrigger>
                <SelectContent>{acts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Raised date</Label>
              <Input type="date" value={form.raised_date} onChange={(e) => setForm({ ...form, raised_date: e.target.value })} data-testid="hindrance-date-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Responsibility</Label>
              <Select value={form.responsibility} onValueChange={(v) => setForm({ ...form, responsibility: v })}>
                <SelectTrigger data-testid="hindrance-responsibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Client", "Contractor", "Vendor", "Force Majeure"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Impact days</Label>
              <Input value={form.impact_days} onChange={(e) => setForm({ ...form, impact_days: e.target.value })} data-testid="hindrance-impact-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Cost impact (₹)</Label>
              <Input value={form.cost_impact} onChange={(e) => setForm({ ...form, cost_impact: e.target.value })} data-testid="hindrance-cost-input" /></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="hindrance-description-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.category} data-testid="hindrance-save-btn">Log hindrance</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
